import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FlaskConical, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { NotificationRecipientLists } from '@/components/form-builder/NotificationRuleBuilder'
import { tenantsApi } from '@/api/tenantsApi'
import type { LisReviewChannel, LisReviewConfig, LisReviewRecipients } from '@/api/lisReviewApi'
import { useLisReviewConfig, useSaveLisReviewConfig } from '@/hooks/useLisReview'
import { useTenant } from '@/providers/TenantProvider'
import { configProblem, DEFAULT_LIS_REVIEW_CONFIG, recipientsForMode } from '@/lib/lisReview'

/**
 * The Notifications page's "LIS notification settings" button and the dialog
 * it opens. A button rather than a panel, so the settings, which a few admins
 * change rarely, do not sit above everyone's notifications.
 */
export function LisReviewSettingsButton() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant='outline' size='sm' onClick={() => setOpen(true)}>
        <FlaskConical className='mr-1.5 h-4 w-4 text-orange-500' />
        LIS notification settings
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>LIS result review notifications</DialogTitle>
            <DialogDescription>
              When a laboratory result arrives it waits for approval before it reaches the form. By
              default everyone who can edit submissions is notified.
            </DialogDescription>
          </DialogHeader>
          {/* Mounted only while open, so each opening starts from what is saved. */}
          {open && <LisReviewSettings onSaved={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Who is told when a LIS result is waiting for approval, and how. The default
 * is everyone who can edit submissions in the tenant; this narrows or widens it.
 */
export function LisReviewSettings({ onSaved }: { onSaved?: () => void }) {
  const { selectedTenantId } = useTenant()
  const { data: saved, isLoading, error } = useLisReviewConfig()
  const save = useSaveLisReviewConfig()
  const [draft, setDraft] = useState<LisReviewConfig>(DEFAULT_LIS_REVIEW_CONFIG)
  const [savedAt, setSavedAt] = useState(false)

  useEffect(() => {
    if (saved) setDraft(saved)
  }, [saved])

  // Same candidates query as the form builder's notification rules, so the two
  // share a cache entry. Only fetched when a picker is actually showing.
  const needsCandidates = draft.recipients.mode !== 'editors'
  const candidates = useQuery({
    queryKey: ['tenants', selectedTenantId, 'notification-recipients'],
    queryFn: () => tenantsApi.getNotificationRecipients(selectedTenantId!),
    enabled: !!selectedTenantId && needsCandidates,
  })

  const problem = configProblem(draft)
  const dirty = !saved || JSON.stringify(saved) !== JSON.stringify(draft)

  const toggleChannel = (channel: LisReviewChannel, checked: boolean) => {
    setSavedAt(false)
    setDraft((d) => ({
      ...d,
      channels: checked ? [...d.channels, channel] : d.channels.filter((c) => c !== channel),
    }))
  }

  const setRecipients = (recipients: LisReviewRecipients) => {
    setSavedAt(false)
    setDraft((d) => ({ ...d, recipients }))
  }

  const userIds = draft.recipients.mode === 'members' ? draft.recipients.userIds : []
  const roleIds = draft.recipients.mode === 'roles' ? draft.recipients.roleIds : []

  return (
    <div>
      {isLoading ? (
        <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
      ) : error ? (
        <p className='text-sm text-destructive'>
          Could not load the settings: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : (
        <div className='space-y-4'>
          <div className='space-y-1'>
            <Label className='text-xs font-normal'>Notify by</Label>
            <div className='flex gap-4'>
              <label className='flex items-center gap-1.5 text-xs'>
                <Checkbox
                  checked={draft.channels.includes('on_screen')}
                  onCheckedChange={(v) => toggleChannel('on_screen', v === true)}
                />
                On screen
              </label>
              <label className='flex items-center gap-1.5 text-xs'>
                <Checkbox
                  checked={draft.channels.includes('email')}
                  onCheckedChange={(v) => toggleChannel('email', v === true)}
                />
                Email
              </label>
            </div>
          </div>

          <div className='space-y-1'>
            <Label className='text-xs font-normal'>Recipients</Label>
            <Select
              value={draft.recipients.mode}
              onValueChange={(v) => setRecipients(recipientsForMode(v as LisReviewRecipients['mode']))}
            >
              <SelectTrigger className='h-8 max-w-sm text-xs'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='editors' className='text-xs'>
                  Everyone who can edit submissions (default)
                </SelectItem>
                <SelectItem value='roles' className='text-xs'>
                  By role
                </SelectItem>
                <SelectItem value='members' className='text-xs'>
                  Specific people
                </SelectItem>
              </SelectContent>
            </Select>
            {(draft.recipients.mode === 'roles' || draft.recipients.mode === 'members') && (
              <div className='max-w-sm'>
                <NotificationRecipientLists
                  mode={draft.recipients.mode}
                  selectedRoleIds={new Set(roleIds)}
                  selectedUserIds={new Set(userIds)}
                  onToggleRole={(id, checked) =>
                    setRecipients({
                      mode: 'roles',
                      roleIds: checked ? [...roleIds, id] : roleIds.filter((r) => r !== id),
                    })
                  }
                  onToggleUser={(id, checked) =>
                    setRecipients({
                      mode: 'members',
                      userIds: checked ? [...userIds, id] : userIds.filter((u) => u !== id),
                    })
                  }
                  candidates={candidates.data}
                  loading={candidates.isLoading}
                  rolesHint='Resolved when the result arrives, so whoever holds the role then is notified.'
                />
                {candidates.error && (
                  <p className='mt-1 text-[11px] text-destructive'>
                    Could not load people and roles. Listing them needs permission to edit forms.
                  </p>
                )}
              </div>
            )}
          </div>

          <label className='flex items-center gap-1.5 text-xs'>
            <Checkbox
              checked={draft.notifyActiveEditor}
              onCheckedChange={(v) => {
                setSavedAt(false)
                setDraft((d) => ({ ...d, notifyActiveEditor: v === true }))
              }}
            />
            Also notify whoever is editing the form
          </label>

          {problem && <p className='text-[11px] text-destructive'>{problem}</p>}
          {save.error && (
            <p className='text-[11px] text-destructive'>
              {save.error instanceof Error ? save.error.message : 'Could not save.'}
            </p>
          )}

          <div className='flex items-center gap-3'>
            <Button
              size='sm'
              disabled={!!problem || !dirty || save.isPending}
              onClick={() =>
                save.mutate(draft, {
                  onSuccess: () => {
                    setSavedAt(true)
                    onSaved?.()
                  },
                })
              }
            >
              {save.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
              Save
            </Button>
            {savedAt && !dirty && <span className='text-xs text-muted-foreground'>Saved</span>}
          </div>
        </div>
      )}
    </div>
  )
}
